import { ButtonBase, Stack, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ChevronLeftDoubleIcon } from '@/components/icons/ChevronLeftDoubleIcon';
import { ChevronRightDoubleIcon } from '@/components/icons/ChevronRightDoubleIcon';
import { Edit05Icon } from '@/components/icons/Edit05Icon';
import { COLORS } from '@/styles/tokens/colors';

import {
  BORDER_COLOR,
  BORDER_COLOR_SELECTED,
  BORDER_COLOR_WARNING,
  BORDER_COLOR_WARNING_SELECTED,
  LEFT_SIDEBAR_COLLAPSED_WIDTH_PX,
  LEFT_SIDEBAR_WIDTH_PX,
} from '../../PresentationTemplateEditor.constants';
import { usePresentationTemplateEditorContext } from '../../PresentationTemplateEditor.context';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorStage,
} from '../../PresentationTemplateEditor.types';
import { PresentationBundleTileBody } from '../presentationTemplate/bundleTile/PresentationTemplateBundleTile';
import {
  getBundleTileHasMoreInformationRequired,
  getBundleTileIcon,
  getBundleTileLabels,
  isBundlePageEditable,
} from '../presentationTemplate/bundleTile/PresentationTemplateBundleTile.utils';

export function PresentationTemplateSlideEditorSidebar() {
  const { isSidebarExpanded } = usePresentationTemplateEditorContext();

  const width = isSidebarExpanded
    ? LEFT_SIDEBAR_WIDTH_PX
    : LEFT_SIDEBAR_COLLAPSED_WIDTH_PX;

  return (
    <Stack
      direction="column"
      width={width}
      height="100%"
      borderRight={`1px solid ${BORDER_COLOR}`}
      alignItems="center"
      flex={`0 0`}
      flexBasis={`${width}px`}
      px={isSidebarExpanded ? 2 : 0}
      py={3}
      sx={{
        transition:
          'width 200ms ease-in-out, padding 200ms ease-in-out, flex-basis 200ms ease-in-out',
      }}
    >
      <Stack
        direction="column"
        flex={1}
        overflow="auto"
        width="100%"
        alignItems="center"
        spacing={2}
      >
        <SidebarBody />
      </Stack>
      <CollapsedSidebarToggle />
    </Stack>
  );
}

function SidebarBody() {
  const {
    isSidebarExpanded,
    bundlesFormArray: { fields: bundles },
  } = usePresentationTemplateEditorContext();

  if (!isSidebarExpanded) {
    return (
      <Edit05Icon
        size={16}
        color={COLORS.NEUTRAL_GRAY[500]}
        style={{
          // because the icon isn't in the center of its container, add
          // a little bit of padding so it lines up visually centered
          paddingLeft: 1,
        }}
      />
    );
  }

  return (
    <>
      <Stack direction="column" width="100%" spacing={0.5} mb={1}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Edit05Icon size={16} />
          <Typography variant="title-xs">Edit pages</Typography>
        </Stack>
        <Typography variant="body-xs">
          These pages in your template can be edited, and may require additional
          steps
        </Typography>
      </Stack>
      {bundles.map((bundle) => {
        if (!isBundlePageEditable(bundle)) return null;

        // Use _rhfId for React key (react-hook-form's tracking ID)
        // but bundle.id for selection (the actual bundle ID)
        return <BundlePreview key={bundle._rhfId} bundle={bundle} />;
      })}
    </>
  );
}

function CollapsedSidebarToggle() {
  const { isSidebarExpanded, setIsSidebarExpanded } =
    usePresentationTemplateEditorContext();

  if (isSidebarExpanded) {
    return (
      <ButtonBase
        onClick={() => setIsSidebarExpanded(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          width: '100%',
          borderRadius: 1,
          px: 1.5,
          py: 1,
          color: COLORS.TEXT.SECONDARY,
          '&:hover': {
            backgroundColor: COLORS.GRAY[50],
            color: COLORS.TEXT.SECONDARY_HOVER,
          },
        }}
      >
        <ChevronLeftDoubleIcon size={16} />
        <Typography variant="body-xs" sx={{ color: 'inherit' }}>
          Collapse
        </Typography>
      </ButtonBase>
    );
  }

  return (
    <IconButton
      variant="transparent"
      icon={ChevronRightDoubleIcon}
      size="sm"
      onClick={() => setIsSidebarExpanded(true)}
      ariaLabel="Expand"
    />
  );
}

interface BundlePreviewProps {
  bundle: PresentationTemplateEditorBundle;
}

function BundlePreview({ bundle }: BundlePreviewProps) {
  const { setStage, selectedBundleId } = usePresentationTemplateEditorContext();

  const icon = getBundleTileIcon(bundle);
  const { label, subLabel } = getBundleTileLabels(bundle);

  const borderColor = useMemo(() => {
    const isSelected = bundle.id === selectedBundleId;
    const needsMoreInfo = getBundleTileHasMoreInformationRequired(bundle);

    if (needsMoreInfo) {
      return isSelected ? BORDER_COLOR_WARNING_SELECTED : BORDER_COLOR_WARNING;
    } else {
      return isSelected ? BORDER_COLOR_SELECTED : BORDER_COLOR;
    }
  }, [bundle, selectedBundleId]);

  const onClick = useCallback(() => {
    setStage(PresentationTemplateEditorStage.Page, bundle.id);
  }, [bundle.id, setStage]);

  return (
    <PresentationBundleTileBody
      borderColor={borderColor}
      isConfigurable
      icon={icon}
      label={label}
      subLabel={subLabel}
      onClick={onClick}
    />
  );
}
